import { useEffect, useState } from "react"
import type { ErrorFetch, SuccessFetch } from "types"
import { CustomertReview, DataList } from "../../types"
import { useApi } from "utils"

type FilteredParams = {
  page?: number
  keyword?: string
  limit?: number
  file_name?: string
  identity_batch?: string
  file_status?: number
}
type ListProductImport = {
  payload: CustomertReview[],
  pagination_data: {
    total: number
    page: number
    size: number
    pages: number
  }
}

export function useDetail({ page, keyword, limit, file_name, file_status, identity_batch }: FilteredParams) {
  const defaultValue = {
    payload: [],
    pagination_data: {
      page: 0,
      pages: 0,
      size: 0,
      total: 0
    }
  }

  const api = useApi()
  const [data, setData] = useState<ListProductImport>(defaultValue)
  const [isLoading, setLoading] = useState(true)

  const params: { [key: string]: number | string | undefined } = {};
  if (page) params.page = page;
  if (keyword) params.keyword = keyword;
  if (limit) params.limit = limit;
  if (file_name) params.file_name = file_name;
  if (file_status) params.file_status = file_status;
  if (identity_batch) params.identity_batch = identity_batch;

  const fetchUrl = () => api.get("/import-customer/view-file", { params })

  const fetchData = () => {
    setLoading(true)

    fetchUrl()
    .then((res) => {setData(res.data)})
    .catch(() => {setData(defaultValue)})
    .finally(() => {setLoading(false)})
  }

  const refetch = () => fetchData()

  useEffect(() => {
    fetchData()

    return () => {
      setData(defaultValue)
      setLoading(true)
    }

    // eslint-disable-next-line
  }, [page, keyword, limit, file_name, file_status])

  return { data, isLoading, refetch }
}

export function useDropdownFileName(file_status: number) {
  const api = useApi()
  const [data, setData] = useState<{ file_name: string }[] | null>(null)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setLoading] = useState(true)

  const params: { [key: string]: number | string | undefined } = {};
  if (file_status) params.file_status = file_status;

  const refetch = (file_status: number) => {
    setLoading(true)

    const params: { [key: string]: number | string | undefined } = {};
    if (file_status) params.file_status = file_status;

    api.get("/import-customer/list-file", { params })
    .then((res: SuccessFetch<{ payload: DataList[] }>) => setData(res.data.payload.map((item: DataList) => ({ file_name: item.file_name }))))
    .catch((err: ErrorFetch<{ detail: { message: string } }>) => setError(err.response.data.detail.message))
    .finally(() => setLoading(false))
  }

  useEffect(() => {
    api.get("/import-customer/list-file", { params })
    .then((res: SuccessFetch<{ payload: DataList[] }>) => setData(res.data.payload.map((item: DataList) => ({ file_name: item.file_name }))))
    .catch((err: ErrorFetch<{ detail: { message: string } }>) => setError(err.response.data.detail.message))
    .finally(() => setLoading(false))
  
    return () => {
      setData(null)
      setError(undefined)
      setLoading(true)
    }
    // eslint-disable-next-line
  }, [])

  return { data, error, isLoading, refetch }
}